import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BookOpen,
  BookOpenCheck,
  Check,
  ChevronRight,
  Code2,
  ExternalLink,
  GitCommit,
  Info,
  Lock,
  MessageSquareOff,
  MessageSquareText,
  Pin,
  PinOff,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Link,
  type MetaFunction,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/ui/alert";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/ui/tooltip";
import { cn } from "@/utils/tailwind";

import { getConceptDefinitionByHref } from "./concepts/registry";
import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import { InlineText } from "./inline-text";
import {
  LearningCatalog,
  LearningCatalogItem,
  LearningCatalogSectionHeader,
} from "./learning-catalog";
import {
  buildBillingHref,
  buildLearningHref,
  buildPricingHref,
  buildRepositoryCommitHref,
  getLearningOrigin,
  type LearningOrigin,
} from "./navigation";
import { useCourseProgress } from "./progress";
import {
  codeFontStyle,
  CodeLine,
  getLineRange,
  getSourceLanguage,
  SourceStatusRow,
  useSourceFileContent,
} from "./source-browser";
import { TerminalReplay } from "./terminal-replay";
import type {
  CodeArtifact,
  CommitHistoryLoaderResponse,
  CourseCommitData,
  CourseLoaderResponse,
  CourseModuleMetadata,
  CourseModuleSummaryData,
  LessonArtifact,
  LessonConceptLinkData,
  LessonData,
  LessonLoaderResponse,
  LessonNavigationData,
  LessonSectionData,
  LessonSummaryData,
  SourceFileContent,
  SourceFilePath,
  SourceSnapshotFileManifest,
} from "./types";
import { getCourseSectionLabel, getShortCommit } from "./utils";

export const meta: MetaFunction = () => {
  return [
    { title: `Course | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Follow guided lessons about how a production-minded Makefile grows commit by commit.",
    },
  ];
};

export const lessonMeta: MetaFunction = ({ data }) => {
  const loaderData = data as LessonLoaderResponse | undefined;
  const lesson = loaderData?.lessonSummary;

  if (!lesson) {
    return [{ title: publicEnv.siteTitle }];
  }

  return [
    {
      title: `${lesson.title} | ${publicEnv.siteTitle}`,
    },
    {
      name: "description",
      content: lesson.description,
    },
  ];
};

export const commitHistoryMeta: MetaFunction = () => {
  return [
    { title: `Repository | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content: "Browse every commit and source snapshot behind Makefile FYI.",
    },
  ];
};

type CourseIndexMode = "active" | "locked" | "visitor";

export default function CourseIndex() {
  const {
    canAccessPaidLessons,
    coreLessonCount,
    dbUser,
    lessonCount,
    modules,
  } = useLoaderData<CourseLoaderResponse>();
  const courseIndexMode = getCourseIndexMode({ canAccessPaidLessons, dbUser });
  const courseProgress = useCourseProgress();
  const completedLessonIds = useMemo(
    () => new Set(courseProgress.completedLessonIds),
    [courseProgress.completedLessonIds],
  );
  const coreModules = modules.filter((module) => module.kind === "module");
  const appendices = modules.filter((module) => module.kind === "appendix");
  const coreLessons = coreModules.flatMap((module) => module.lessons);
  const appendixLessonCount = lessonCount - coreLessonCount;
  const accessibleLessons = coreLessons.filter((lesson) => {
    return lesson.access === "free" || canAccessPaidLessons;
  });
  const lastLesson = accessibleLessons.find((lesson) => {
    return getShortCommit(lesson.commit) === courseProgress.lastLessonCommit;
  });
  const firstIncompleteLesson = accessibleLessons.find((lesson) => {
    return !completedLessonIds.has(lesson.id);
  });
  const continueLesson =
    lastLesson && !completedLessonIds.has(lastLesson.id)
      ? lastLesson
      : (firstIncompleteLesson ?? lastLesson ?? accessibleLessons[0]);
  const completedLessonCount = coreLessons.filter((lesson) => {
    return completedLessonIds.has(lesson.id);
  }).length;
  const areAccessibleLessonsComplete =
    accessibleLessons.length > 0 &&
    accessibleLessons.every((lesson) => completedLessonIds.has(lesson.id));
  const courseCatalogs = [
    {
      countLabel: `${formatArtifactCount(coreModules.length, "module")} · ${formatArtifactCount(coreLessonCount, "lesson")}`,
      id: "course-path",
      label: "Course path",
      modules: coreModules,
    },
    {
      countLabel: `${formatArtifactCount(appendices.length, "appendix", "appendices")} · ${formatArtifactCount(appendixLessonCount, "lesson")}`,
      id: "supporting-reference",
      label:
        appendices.length === 1
          ? "Supporting reference"
          : "Supporting references",
      modules: appendices,
    },
  ].filter((catalog) => catalog.modules.length > 0);

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-8 pb-20 sm:w-[calc(100%-3rem)] sm:gap-12 sm:pb-28">
        <section className="mx-auto w-full max-w-5xl pt-10 sm:pt-14 lg:pt-16">
          <div className="flex max-w-3xl flex-col gap-7">
            <Badge className="w-fit font-mono shadow-xs" variant="secondary">
              <BookOpen />
              {formatArtifactCount(coreLessonCount, "core lesson")} ·{" "}
              {formatArtifactCount(appendixLessonCount, "appendix lesson")}
            </Badge>

            <div className="flex flex-col gap-4">
              <h1 className="max-w-2xl text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                Course
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
                Follow the milestone lessons in order, continue where you left
                off, open any module to revisit an engineering decision, or use
                the appendix as a supporting reference.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {courseIndexMode === "visitor" && continueLesson && (
                <Button asChild className="w-full justify-center sm:w-auto">
                  <Link
                    to={buildLearningHref({
                      destination: `/course/${getShortCommit(
                        continueLesson.commit,
                      )}`,
                      from: "/course",
                    })}
                  >
                    <BookOpen />
                    {lastLesson || completedLessonCount > 0
                      ? "Continue course"
                      : "Start the first free lesson"}
                  </Link>
                </Button>
              )}
              {courseIndexMode === "active" && continueLesson && (
                <Button asChild className="w-full justify-center sm:w-auto">
                  <Link
                    to={buildLearningHref({
                      destination: `/course/${getShortCommit(
                        continueLesson.commit,
                      )}`,
                      from: "/course",
                    })}
                  >
                    <BookOpen />
                    {completedLessonCount === coreLessonCount
                      ? "Review the course"
                      : "Continue course"}
                  </Link>
                </Button>
              )}
              {courseIndexMode === "locked" && (
                <Button asChild className="w-full justify-center sm:w-auto">
                  <Link to="/billing">
                    <Lock />
                    Unlock full access
                  </Link>
                </Button>
              )}
              {courseIndexMode !== "active" && (
                <Button
                  asChild
                  className="w-full justify-center sm:w-auto"
                  variant="outline"
                >
                  {courseIndexMode === "visitor" ? (
                    <Link to="/pricing">View pricing</Link>
                  ) : continueLesson ? (
                    <Link
                      to={buildLearningHref({
                        destination: `/course/${getShortCommit(
                          continueLesson.commit,
                        )}`,
                        from: "/course",
                      })}
                    >
                      {areAccessibleLessonsComplete
                        ? "Review free lessons"
                        : "Continue free lessons"}
                    </Link>
                  ) : (
                    <Link to="/course">Browse course</Link>
                  )}
                </Button>
              )}
            </div>

            {courseProgress.isReady && completedLessonCount > 0 && (
              <p className="text-muted-foreground font-mono text-xs">
                {completedLessonCount}/{coreLessonCount} core lessons complete
                on this device
              </p>
            )}
          </div>
        </section>

        <nav
          aria-label="Course modules and appendix"
          className="grid scroll-mt-8 gap-6"
          id="course-path"
        >
          {courseCatalogs.map((catalog) => (
            <LearningCatalog
              key={catalog.id}
              countLabel={catalog.countLabel}
              label={catalog.label}
            >
              {catalog.modules.map((module, moduleIndex) => (
                <CourseModule
                  key={module.id}
                  appendixCount={appendices.length}
                  canAccessPaidLessons={canAccessPaidLessons}
                  completedLessonIds={completedLessonIds}
                  module={module}
                  moduleIndex={moduleIndex}
                  showProgress={courseProgress.isReady}
                />
              ))}
            </LearningCatalog>
          ))}
        </nav>

        {courseIndexMode !== "active" && (
          <CourseAccessPrompt dbUser={dbUser} lessonCount={lessonCount} />
        )}
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

interface CourseModuleProps {
  appendixCount: number;
  canAccessPaidLessons: boolean;
  completedLessonIds: ReadonlySet<string>;
  module: CourseModuleSummaryData;
  moduleIndex: number;
  showProgress: boolean;
}

function CourseModule({
  appendixCount,
  canAccessPaidLessons,
  completedLessonIds,
  module,
  moduleIndex,
  showProgress,
}: CourseModuleProps) {
  const completedLessonCount = module.lessons.filter((lesson) => {
    return completedLessonIds.has(lesson.id);
  }).length;
  const isAppendix = module.kind === "appendix";
  const sectionNumber = String(moduleIndex + 1).padStart(2, "0");
  const eyebrow = isAppendix
    ? appendixCount === 1
      ? "Appendix"
      : `Appendix ${sectionNumber}`
    : `Module ${sectionNumber}`;
  const lessonNumberPrefix = isAppendix
    ? appendixCount === 1
      ? "A"
      : `A${moduleIndex + 1}`
    : String(moduleIndex + 1);

  return (
    <details
      open
      className="group scroll-mt-20 border-t first:border-t-0"
      id={module.id}
    >
      <summary className="bg-muted/20 cursor-pointer list-none px-4 py-4 [&::-webkit-details-marker]:hidden">
        <LearningCatalogSectionHeader
          description={module.description}
          eyebrow={eyebrow}
          title={module.title}
          meta={
            showProgress && completedLessonCount > 0
              ? `${completedLessonCount}/${module.lessonCount} complete`
              : formatArtifactCount(module.lessonCount, "lesson")
          }
          trailing={
            <ChevronRight className="size-4 transition-transform group-open:rotate-90" />
          }
        />
      </summary>

      <ol className="grid gap-1 p-2">
        {module.lessons.map((lesson, lessonIndex) => {
          const canOpenLesson =
            lesson.access === "free" || canAccessPaidLessons;
          const lessonPath = `/course/${getShortCommit(lesson.commit)}`;
          const lessonHref = buildLearningHref({
            destination: lessonPath,
            from: `/course#${module.id}`,
          });

          return (
            <CourseLessonRow
              key={lesson.id}
              canOpenLesson={canOpenLesson}
              href={lessonHref}
              isComplete={completedLessonIds.has(lesson.id)}
              lesson={lesson}
              lessonIndex={lessonIndex}
              lessonNumberPrefix={lessonNumberPrefix}
              showAccessBadge={!canAccessPaidLessons}
            />
          );
        })}
      </ol>
    </details>
  );
}

interface CourseLessonRowProps {
  canOpenLesson: boolean;
  href: string;
  isComplete: boolean;
  lesson: LessonSummaryData;
  lessonIndex: number;
  lessonNumberPrefix: string;
  showAccessBadge: boolean;
}

function CourseLessonRow({
  canOpenLesson,
  href,
  isComplete,
  lesson,
  lessonIndex,
  lessonNumberPrefix,
  showAccessBadge,
}: CourseLessonRowProps) {
  const shortCommit = getShortCommit(lesson.commit);
  const lessonNumber = `${lessonNumberPrefix}.${lessonIndex + 1}`;

  return (
    <LearningCatalogItem
      eyebrow={`Lesson ${lessonNumber} · commit ${shortCommit}`}
      href={href}
      isMuted={!canOpenLesson}
      title={lesson.title}
      accessBadge={
        showAccessBadge ? <LessonAccessBadge lesson={lesson} /> : undefined
      }
      ariaLabel={
        canOpenLesson ? undefined : `Preview full-access lesson ${lesson.title}`
      }
      marker={
        isComplete ? (
          <>
            <Check aria-hidden="true" className="size-3.5" />
            <span className="sr-only">Complete</span>
          </>
        ) : (
          lessonNumber
        )
      }
      metadata={[
        formatArtifactCount(lesson.codeArtifactCount, "code clip"),
        formatArtifactCount(lesson.terminalArtifactCount, "terminal replay"),
      ]}
      trailing={
        canOpenLesson ? (
          <ChevronRight className="size-4" />
        ) : (
          <Lock className="size-4" />
        )
      }
    />
  );
}

export function CommitHistoryPage() {
  const { canAccessPaidLessons, commits, dbUser, modules } =
    useLoaderData<CommitHistoryLoaderResponse>();
  const [searchParams] = useSearchParams();
  const lessonFilter = getCommitLessonFilter(searchParams);
  const order = getCommitOrder(searchParams);
  const moduleId = getCommitModuleFilter(searchParams, modules);
  const visibleCommits = getVisibleCommits({
    commits,
    lessonFilter,
    moduleId,
    order,
  });

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-8 pb-20 sm:w-[calc(100%-3rem)] sm:gap-12 sm:pb-28">
        <section className="mx-auto w-full max-w-5xl pt-10 sm:pt-14 lg:pt-16">
          <div className="flex max-w-3xl flex-col gap-7">
            <Badge className="w-fit font-mono shadow-xs" variant="secondary">
              <GitCommit />
              {commits.length} commits
            </Badge>

            <div className="grid gap-4">
              <h1 className="max-w-3xl text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                Repository
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
                Explore the complete chronological history, open any source
                snapshot, or filter down to the milestone commits used by the
                guided course.
              </p>
              <Link
                className="underline-decoration text-muted-foreground hover:text-foreground w-fit text-sm underline transition"
                to="/guide"
              >
                See how Repository fits into the course
              </Link>
              {!canAccessPaidLessons && (
                <p className="text-muted-foreground text-sm leading-6">
                  Commit subjects and history are public. A lock marks source
                  snapshots included with full access.
                </p>
              )}
            </div>
          </div>
        </section>

        <section
          aria-labelledby="commit-history-filters-title"
          className="bg-card/90 overflow-hidden rounded-md border shadow-sm"
        >
          <div className="bg-muted/30 grid gap-4 border-b px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <h2
                className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase"
                id="commit-history-filters-title"
              >
                Filters
              </h2>
              <p className="text-muted-foreground font-mono text-xs">
                {formatArtifactCount(visibleCommits.length, "commit")}
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <fieldset className="grid gap-2">
                <legend className="text-muted-foreground mb-1 text-xs font-medium">
                  Content
                </legend>
                <div className="flex flex-wrap gap-2">
                  <CommitFilterButton
                    isActive={lessonFilter === "all"}
                    label="All commits"
                    to={getCommitHistoryUrl(searchParams, { lessons: null })}
                  />
                  <CommitFilterButton
                    isActive={lessonFilter === "lessons"}
                    label="Lesson commits"
                    to={getCommitHistoryUrl(searchParams, {
                      lessons: "only",
                    })}
                  />
                </div>
              </fieldset>

              <fieldset className="grid gap-2">
                <legend className="text-muted-foreground mb-1 text-xs font-medium">
                  Order
                </legend>
                <div className="flex flex-wrap gap-2">
                  <CommitFilterButton
                    isActive={order === "time"}
                    label="Timeline"
                    to={getCommitHistoryUrl(searchParams, { order: null })}
                  />
                  <CommitFilterButton
                    isActive={order === "course"}
                    label="Course path"
                    to={getCommitHistoryUrl(searchParams, { order: "course" })}
                  />
                </div>
              </fieldset>

              <fieldset className="grid gap-2 lg:col-span-2">
                <legend className="text-muted-foreground mb-1 text-xs font-medium">
                  Course section
                </legend>
                <div className="flex flex-wrap gap-2">
                  <CommitFilterButton
                    isActive={!moduleId}
                    label="All sections"
                    to={getCommitHistoryUrl(searchParams, { module: null })}
                  />
                  {modules.map((module) => (
                    <CommitFilterButton
                      key={module.id}
                      isActive={moduleId === module.id}
                      label={getCourseSectionLabel(module)}
                      to={getCommitHistoryUrl(searchParams, {
                        module: module.id,
                      })}
                    />
                  ))}
                </div>
              </fieldset>
            </div>
          </div>

          {visibleCommits.length === 0 && (
            <p className="text-muted-foreground px-4 py-8 text-center text-sm">
              No commits match these filters.
            </p>
          )}

          <div className="divide-y md:hidden">
            {visibleCommits.map((commit) => (
              <CommitHistoryCard
                key={commit.shortCommit}
                canAccessPaidLessons={canAccessPaidLessons}
                commit={commit}
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[56rem] text-left text-sm">
              <thead className="text-muted-foreground border-b text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Commit</th>
                  <th className="px-4 py-3 font-medium">Subject</th>
                  <th className="px-4 py-3 font-medium">Course section</th>
                  <th className="px-4 py-3 font-medium">Lesson</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visibleCommits.map((commit) => (
                  <CommitHistoryRow
                    key={commit.shortCommit}
                    canAccessPaidLessons={canAccessPaidLessons}
                    commit={commit}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

interface CommitFilterButtonProps {
  isActive: boolean;
  label: string;
  to: string;
}

function CommitFilterButton({ isActive, label, to }: CommitFilterButtonProps) {
  return (
    <Button asChild size="sm" variant={isActive ? "default" : "outline"}>
      <Link aria-current={isActive ? "true" : undefined} to={to}>
        {label}
      </Link>
    </Button>
  );
}

interface CommitHistoryRowProps {
  canAccessPaidLessons: boolean;
  commit: CourseCommitData;
}

function CommitHistoryRow({
  canAccessPaidLessons,
  commit,
}: CommitHistoryRowProps) {
  const lesson = commit.lesson;
  const canOpenLesson =
    Boolean(lesson) && (lesson?.access === "free" || canAccessPaidLessons);

  return (
    <tr>
      <td className="text-muted-foreground px-4 py-3 font-mono text-xs whitespace-nowrap">
        {commit.date}
      </td>
      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
        <Link
          className="underline-decoration inline-flex items-center gap-1 underline"
          to={buildRepositoryCommitHref({ commit: commit.commit })}
        >
          {commit.shortCommit}
          {commit.sourceAccess === "paid" && !canAccessPaidLessons && (
            <Lock className="size-3" />
          )}
        </Link>
      </td>
      <td className="min-w-80 px-4 py-3">{commit.subject}</td>
      <td className="text-muted-foreground px-4 py-3 text-xs whitespace-nowrap">
        {commit.module ? getCourseSectionLabel(commit.module) : "-"}
      </td>
      <td className="px-4 py-3 text-xs whitespace-nowrap">
        {!lesson && <span className="text-muted-foreground">No lesson</span>}
        {lesson && canOpenLesson && (
          <Link
            className="underline-decoration font-medium underline"
            to={buildLearningHref({
              destination: `/course/${commit.shortCommit}`,
              from: "/commits",
            })}
          >
            <InlineText text={lesson.title} />
          </Link>
        )}
        {lesson && !canOpenLesson && (
          <Link
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 underline-offset-4 hover:underline"
            to={buildLearningHref({
              destination: `/course/${commit.shortCommit}`,
              from: "/commits",
            })}
          >
            <Lock className="size-3.5" />
            Preview lesson
          </Link>
        )}
      </td>
    </tr>
  );
}

type CommitHistoryCardProps = CommitHistoryRowProps;

function CommitHistoryCard({
  canAccessPaidLessons,
  commit,
}: CommitHistoryCardProps) {
  const lesson = commit.lesson;
  const canOpenLesson =
    Boolean(lesson) && (lesson?.access === "free" || canAccessPaidLessons);
  const lessonPath = lesson ? `/course/${commit.shortCommit}` : null;
  const lessonHref = lessonPath
    ? buildLearningHref({ destination: lessonPath, from: "/commits" })
    : null;

  return (
    <article className="grid gap-3 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          className="underline-decoration inline-flex items-center gap-1 font-mono text-xs underline"
          to={buildRepositoryCommitHref({ commit: commit.commit })}
        >
          {commit.shortCommit}
          {commit.sourceAccess === "paid" && !canAccessPaidLessons && (
            <Lock className="size-3" />
          )}
        </Link>
        <time className="text-muted-foreground font-mono text-xs">
          {commit.date}
        </time>
      </div>
      <p className="text-sm leading-6">{commit.subject}</p>
      <div className="flex flex-wrap items-center gap-2">
        {commit.module && (
          <Badge variant="outline">
            {getCourseSectionLabel(commit.module)}
          </Badge>
        )}
        {!lesson && (
          <span className="text-muted-foreground text-xs">No lesson</span>
        )}
        {lesson && lessonHref && (
          <Link
            className="underline-decoration inline-flex items-center gap-1 text-xs font-medium underline"
            to={lessonHref}
          >
            {!canOpenLesson && <Lock className="size-3.5" />}
            {canOpenLesson ? (
              <InlineText text={lesson.title} />
            ) : (
              "Preview lesson"
            )}
          </Link>
        )}
      </div>
    </article>
  );
}

export function LessonPage() {
  const {
    canAccessPaidLessons,
    dbUser,
    lesson,
    lessonSummary,
    module,
    navigation,
    repositoryCommit,
    repositoryCommitCount,
  } = useLoaderData<LessonLoaderResponse>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const origin = getLearningOrigin(searchParams.get("from"));

  if (!lesson) {
    return (
      <CourseLessonAccessPreview
        dbUser={dbUser}
        lesson={lessonSummary}
        module={module}
        navigation={navigation}
        origin={origin}
        repositoryCommit={repositoryCommit}
        returnTo={`${location.pathname}${location.search}`}
      />
    );
  }

  return (
    <CourseLesson
      canAccessPaidLessons={canAccessPaidLessons}
      dbUser={dbUser}
      lesson={lesson}
      module={module}
      navigation={navigation}
      origin={origin}
      purchaseSucceeded={searchParams.get("purchase") === "success"}
      repositoryCommit={repositoryCommit}
      repositoryCommitCount={repositoryCommitCount}
    />
  );
}

function getLessonPositionLabel(
  module: CourseModuleMetadata,
  navigation: LessonNavigationData,
) {
  return module.kind === "appendix"
    ? `A.${navigation.position}/${navigation.total}`
    : `${navigation.position}/${navigation.total}`;
}

function getLessonPositionAriaLabel(
  module: CourseModuleMetadata,
  navigation: LessonNavigationData,
) {
  const pathLabel = module.kind === "appendix" ? "Appendix lesson" : "Lesson";

  return `${pathLabel} ${navigation.position} of ${navigation.total}`;
}

function CourseLessonAccessPreview({
  dbUser,
  lesson,
  module,
  navigation,
  origin,
  repositoryCommit,
  returnTo,
}: {
  dbUser: LessonLoaderResponse["dbUser"];
  lesson: LessonSummaryData;
  module: CourseModuleMetadata;
  navigation: LessonNavigationData;
  origin: LearningOrigin | null;
  repositoryCommit: CourseCommitData;
  returnTo: string;
}) {
  const shortCommit = getShortCommit(lesson.commit);
  const lessonPositionLabel = getLessonPositionLabel(module, navigation);
  const unlockHref = dbUser
    ? buildBillingHref({ returnTo })
    : buildPricingHref({ returnTo });

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-10 pb-20 sm:w-[calc(100%-3rem)] sm:gap-14 sm:pb-28">
        <header className="grid max-w-3xl gap-7 pt-10 sm:pt-14 lg:pt-16">
          <Link
            className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm transition"
            to={origin?.href ?? `/course#${module.id}`}
          >
            <ArrowLeft className="size-4" />
            {origin?.label ?? "Back to course"}
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <Badge className="w-fit font-mono shadow-xs" variant="secondary">
              <GitCommit />
              Lesson {shortCommit}
            </Badge>
            <Badge className="w-fit shadow-xs" variant="outline">
              <BookOpen />
              {getCourseSectionLabel(module)}
            </Badge>
            <Badge
              aria-label={getLessonPositionAriaLabel(module, navigation)}
              className="w-fit font-mono shadow-xs"
              variant="outline"
            >
              {lessonPositionLabel}
            </Badge>
            <Badge className="w-fit font-mono shadow-xs" variant="outline">
              <Lock />
              Full access
            </Badge>
          </div>

          <div className="grid gap-4">
            <h1 className="text-4xl leading-tight font-semibold text-balance sm:text-5xl">
              <InlineText text={lesson.title} />
            </h1>
            <p className="text-muted-foreground text-lg leading-8 text-pretty">
              <InlineText text={lesson.description} />
            </p>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <span>
              {formatArtifactCount(lesson.codeArtifactCount, "code clip")}
            </span>
            <span>
              {formatArtifactCount(
                lesson.terminalArtifactCount,
                "terminal replay",
              )}
            </span>
            <span>Commit {repositoryCommit.shortCommit}</span>
          </div>
        </header>

        <section className="bg-card grid min-h-72 place-items-center rounded-md border p-6 shadow-sm">
          <div className="grid max-w-lg justify-items-center gap-5 text-center">
            <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-12 items-center justify-center rounded-md shadow-xs ring-1">
              <Lock className="size-5" />
            </span>
            <div className="grid gap-2">
              <h2 className="text-xl font-semibold">Continue this lesson</h2>
              <p className="text-muted-foreground leading-7">
                The lesson overview and repository context remain public. Full
                access unlocks the explanation, focused code clips, terminal
                replays, concepts, and every future lesson.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild>
                <Link to={unlockHref}>Unlock full access</Link>
              </Button>
              <Button asChild variant="outline">
                <Link
                  to={buildRepositoryCommitHref({
                    commit: repositoryCommit.commit,
                    from: `/course/${shortCommit}`,
                  })}
                >
                  Inspect the commit
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

interface CourseLessonProps {
  canAccessPaidLessons: LessonLoaderResponse["canAccessPaidLessons"];
  dbUser: LessonLoaderResponse["dbUser"];
  lesson: LessonData;
  module: LessonLoaderResponse["module"];
  navigation: LessonNavigationData;
  origin: LearningOrigin | null;
  purchaseSucceeded: boolean;
  repositoryCommit: LessonLoaderResponse["repositoryCommit"];
  repositoryCommitCount: number;
}

function CourseLesson({
  canAccessPaidLessons,
  dbUser,
  lesson,
  module,
  navigation,
  origin,
  purchaseSucceeded,
  repositoryCommit,
  repositoryCommitCount,
}: CourseLessonProps) {
  const shortCommit = getShortCommit(lesson.commit);
  const lessonPositionLabel = getLessonPositionLabel(module, navigation);
  const { completedLessonIds, recordLessonVisit, setLessonCompleted } =
    useCourseProgress();
  const isLessonComplete = completedLessonIds.includes(lesson.id);

  useEffect(() => {
    if (module.kind === "module") {
      recordLessonVisit(shortCommit);
    }
  }, [module.kind, recordLessonVisit, shortCommit]);

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-8 pb-20 sm:w-[calc(100%-3rem)] sm:gap-12 sm:pb-28">
        <section className="mx-auto w-full max-w-5xl pt-10 sm:pt-14 lg:pt-16">
          <div className="flex max-w-3xl flex-col gap-7">
            {origin && (
              <Link
                className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm transition"
                to={origin.href}
              >
                <ArrowLeft className="size-4" />
                {origin.label}
              </Link>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="w-fit font-mono shadow-xs" variant="secondary">
                <GitCommit />
                Lesson {shortCommit}
              </Badge>
              <Badge asChild className="w-fit shadow-xs" variant="outline">
                <Link to={`/course#${module.id}`}>
                  <BookOpen />
                  {getCourseSectionLabel(module)}
                </Link>
              </Badge>
              <Badge
                aria-label={getLessonPositionAriaLabel(module, navigation)}
                className="w-fit font-mono shadow-xs"
                variant="outline"
              >
                {lessonPositionLabel}
              </Badge>
            </div>

            <div className="flex flex-col gap-4">
              <h1 className="max-w-3xl text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                <InlineText text={lesson.title} />
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
                <InlineText text={lesson.description} />
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button
                asChild
                className="w-full justify-center sm:w-auto"
                variant="outline"
              >
                <Link
                  to={buildRepositoryCommitHref({
                    commit: lesson.commit,
                    filePath: getDefaultSourceFileMetadata(lesson).path,
                    from: `/course/${shortCommit}`,
                  })}
                >
                  <GitCommit />
                  Explore commit {shortCommit}
                </Link>
              </Button>
              <Button
                asChild
                className="w-full justify-center sm:w-auto"
                variant="outline"
              >
                <a
                  href={getLessonGitHubUrl(lesson)}
                  rel="noreferrer"
                  target="_blank"
                >
                  View {shortCommit} on GitHub
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {purchaseSucceeded && (
          <Alert>
            <Check className="size-4" />
            <AlertTitle>Full access unlocked</AlertTitle>
            <AlertDescription>
              Your purchase is confirmed, and the complete learning library is
              now available.
            </AlertDescription>
          </Alert>
        )}

        <nav
          aria-label="Lesson sections"
          className="scroll-mt-8"
          id="lesson-path"
        >
          <div className="bg-card/90 overflow-hidden rounded-md border shadow-sm">
            <div className="bg-muted/30 flex items-center justify-between gap-3 border-b px-4 py-3">
              <p className="text-muted-foreground text-xs font-medium tracking-[0.18em] uppercase">
                Lesson path
              </p>
              <p className="text-muted-foreground font-mono text-xs">
                {lesson.sections.length} sections
              </p>
            </div>

            <ol className="grid gap-1 p-2">
              {lesson.sections.map((section, index) => {
                const codeClipCount = getLessonSectionArtifactCount(
                  section,
                  "code",
                );
                const terminalRunCount = getLessonSectionArtifactCount(
                  section,
                  "terminal",
                );

                return (
                  <li key={section.id}>
                    <a
                      className="hover:bg-accent/70 group grid h-full grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-3 rounded-sm px-2 py-2.5 transition sm:grid-cols-[2rem_minmax(0,1fr)_5.75rem_7.75rem_auto]"
                      href={`#${section.id}`}
                    >
                      <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-7 items-center justify-center rounded-md font-mono text-xs shadow-xs ring-1">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">
                          <InlineText text={section.title} />
                        </span>
                        <span className="text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs sm:hidden">
                          <span>
                            {formatArtifactCount(codeClipCount, "code clip")}
                          </span>
                          <span>
                            {formatArtifactCount(
                              terminalRunCount,
                              "terminal replay",
                            )}
                          </span>
                        </span>
                      </span>
                      <span className="text-muted-foreground hidden text-right text-xs sm:block">
                        {formatArtifactCount(codeClipCount, "code clip")}
                      </span>
                      <span className="text-muted-foreground hidden text-right text-xs sm:block">
                        {formatArtifactCount(
                          terminalRunCount,
                          "terminal replay",
                        )}
                      </span>
                      <ChevronRight className="text-muted-foreground group-hover:text-foreground size-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>
        </nav>

        <section
          className="border-border/70 flex scroll-mt-8 flex-col border-t pt-5 sm:pt-6"
          id="lesson"
        >
          <article className="mx-auto flex w-full max-w-5xl min-w-0 flex-col gap-12">
            {lesson.sections.map((section, index) => (
              <LessonSection
                key={`${lesson.id}-${section.id}`}
                canAccessPaidConcepts={canAccessPaidLessons}
                isSeparated={index > 0}
                lesson={lesson}
                originPath={`/course/${shortCommit}`}
                repositoryCommit={repositoryCommit}
                repositoryCommitCount={repositoryCommitCount}
                section={section}
                sectionIndex={index}
              />
            ))}
          </article>
        </section>

        <LessonCompletionNavigation
          canAccessPaidLessons={canAccessPaidLessons}
          courseSectionKind={module.kind}
          isComplete={isLessonComplete}
          navigation={navigation}
          originPath={origin?.href}
          onCompleteChange={(isComplete) => {
            setLessonCompleted(lesson.id, isComplete);
          }}
        />
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

interface LessonCompletionNavigationProps {
  canAccessPaidLessons: boolean;
  courseSectionKind: CourseModuleMetadata["kind"];
  isComplete: boolean;
  navigation: LessonNavigationData;
  onCompleteChange: (isComplete: boolean) => void;
  originPath?: string;
}

function LessonCompletionNavigation({
  canAccessPaidLessons,
  courseSectionKind,
  isComplete,
  navigation,
  onCompleteChange,
  originPath,
}: LessonCompletionNavigationProps) {
  return (
    <section
      aria-labelledby="lesson-completion-title"
      className="border-border/70 grid gap-6 border-t pt-8 sm:pt-10"
    >
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="grid gap-2">
          <h2 className="text-xl font-semibold" id="lesson-completion-title">
            {isComplete ? "Lesson complete" : "Finished this lesson?"}
          </h2>
          <p className="text-muted-foreground text-sm leading-6">
            {courseSectionKind === "appendix"
              ? "Completion is saved on this device for this appendix."
              : "Completion is saved on this device and used to resume the course."}
          </p>
        </div>
        <Button
          type="button"
          variant={isComplete ? "outline" : "default"}
          onClick={() => onCompleteChange(!isComplete)}
        >
          <Check />
          {isComplete ? "Mark incomplete" : "Mark lesson complete"}
        </Button>
      </div>

      <nav
        aria-label="Lesson navigation"
        className="border-border grid divide-y border-y sm:grid-cols-2 sm:divide-y-0"
      >
        {navigation.previous ? (
          <LessonNavigationLink
            canAccessPaidLessons={canAccessPaidLessons}
            direction="previous"
            lesson={navigation.previous}
            originPath={originPath}
          />
        ) : (
          <span className="hidden sm:block" />
        )}

        {navigation.next ? (
          <LessonNavigationLink
            canAccessPaidLessons={canAccessPaidLessons}
            direction="next"
            lesson={navigation.next}
            originPath={originPath}
          />
        ) : (
          <Link
            className="hover:bg-accent/60 group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-4 text-right transition sm:border-l"
            to="/course"
          >
            <span className="grid gap-1">
              <span className="text-muted-foreground text-xs">
                {courseSectionKind === "appendix"
                  ? "Appendix complete"
                  : "Core course complete"}
              </span>
              <span className="font-medium">Return to the course path</span>
            </span>
            <ArrowRight className="text-muted-foreground size-4" />
          </Link>
        )}
      </nav>
    </section>
  );
}

interface LessonNavigationLinkProps {
  canAccessPaidLessons: boolean;
  direction: "next" | "previous";
  lesson: LessonSummaryData;
  originPath?: string;
}

function LessonNavigationLink({
  canAccessPaidLessons,
  direction,
  lesson,
  originPath,
}: LessonNavigationLinkProps) {
  const canOpenLesson = lesson.access === "free" || canAccessPaidLessons;
  const lessonPath = `/course/${getShortCommit(lesson.commit)}`;
  const isNext = direction === "next";

  return (
    <Link
      to={buildLearningHref({ destination: lessonPath, from: originPath })}
      className={cn(
        "hover:bg-accent/60 group grid items-center gap-3 px-3 py-4 transition",
        isNext
          ? "grid-cols-[minmax(0,1fr)_auto] text-right sm:border-l"
          : "grid-cols-[auto_minmax(0,1fr)]",
      )}
    >
      {!isNext &&
        (canOpenLesson ? (
          <ArrowLeft className="text-muted-foreground size-4" />
        ) : (
          <Lock className="text-muted-foreground size-4" />
        ))}
      <span className="grid min-w-0 gap-1">
        <span className="text-muted-foreground text-xs">
          {canOpenLesson
            ? `${isNext ? "Next" : "Previous"} lesson`
            : "Full-access preview"}
        </span>
        <span className="font-medium">
          <InlineText text={lesson.title} />
        </span>
      </span>
      {isNext &&
        (canOpenLesson ? (
          <ArrowRight className="text-muted-foreground size-4" />
        ) : (
          <Lock className="text-muted-foreground size-4" />
        ))}
    </Link>
  );
}

function CourseAccessPrompt({
  dbUser,
  lessonCount,
}: {
  dbUser: CourseLoaderResponse["dbUser"];
  lessonCount: number;
}) {
  return (
    <section
      aria-labelledby="course-access-title"
      className="border-border/70 flex flex-col items-start justify-between gap-5 border-t pt-8 sm:flex-row sm:items-center sm:pt-10"
    >
      <div className="grid max-w-2xl gap-2">
        <h2 className="text-xl font-semibold" id="course-access-title">
          Continue beyond the free foundations
        </h2>
        <p className="text-muted-foreground leading-7">
          Full access unlocks all {lessonCount} lessons, every concept, complete
          repository snapshots, and interactive terminal replays.
        </p>
      </div>
      <Button asChild className="shrink-0">
        <Link to={dbUser ? "/billing" : "/pricing"}>
          Unlock full access
          <ArrowRight />
        </Link>
      </Button>
    </section>
  );
}

interface LessonAccessBadgeProps {
  lesson: LessonSummaryData;
}

function LessonAccessBadge({ lesson }: LessonAccessBadgeProps) {
  if (lesson.access === "free") {
    return (
      <Badge className="font-mono text-[10px]" variant="secondary">
        Free
      </Badge>
    );
  }

  return (
    <Badge className="font-mono text-[10px]" variant="outline">
      <Lock />
      Full access
    </Badge>
  );
}

function getCourseIndexMode({
  canAccessPaidLessons,
  dbUser,
}: {
  canAccessPaidLessons: boolean;
  dbUser: CourseLoaderResponse["dbUser"];
}): CourseIndexMode {
  if (!dbUser) {
    return "visitor";
  }

  return canAccessPaidLessons ? "active" : "locked";
}

function getLessonGitHubUrl(lesson: LessonData) {
  return `https://github.com/quietcolin/makefile-fyi/tree/${lesson.commit}`;
}

type CommitLessonFilter = "all" | "lessons";
type CommitOrder = "course" | "time";

function getCommitLessonFilter(
  searchParams: URLSearchParams,
): CommitLessonFilter {
  return searchParams.get("lessons") === "only" ? "lessons" : "all";
}

function getCommitOrder(searchParams: URLSearchParams): CommitOrder {
  return searchParams.get("order") === "course" ? "course" : "time";
}

function getCommitModuleFilter(
  searchParams: URLSearchParams,
  modules: readonly CourseModuleMetadata[],
) {
  const moduleId = searchParams.get("module");

  return modules.some((module) => module.id === moduleId) ? moduleId : null;
}

function getVisibleCommits({
  commits,
  lessonFilter,
  moduleId,
  order,
}: {
  commits: readonly CourseCommitData[];
  lessonFilter: CommitLessonFilter;
  moduleId: string | null;
  order: CommitOrder;
}) {
  const visibleCommits = commits.filter((commit) => {
    if (lessonFilter === "lessons" && !commit.lesson) {
      return false;
    }

    if (moduleId && commit.module?.id !== moduleId) {
      return false;
    }

    return true;
  });

  return [...visibleCommits].sort((first, second) => {
    if (order === "time") {
      return first.timelineOrder - second.timelineOrder;
    }

    if (first.courseOrder === null && second.courseOrder === null) {
      return first.timelineOrder - second.timelineOrder;
    }

    if (first.courseOrder === null) {
      return 1;
    }

    if (second.courseOrder === null) {
      return -1;
    }

    return first.courseOrder - second.courseOrder;
  });
}

function getCommitHistoryUrl(
  searchParams: URLSearchParams,
  patch: {
    lessons?: "only" | null;
    module?: string | null;
    order?: "course" | null;
  },
) {
  const nextSearchParams = new URLSearchParams(searchParams);

  for (const [key, value] of Object.entries(patch)) {
    if (!value) {
      nextSearchParams.delete(key);
      continue;
    }

    nextSearchParams.set(key, value);
  }

  const query = nextSearchParams.toString();

  return query ? `/commits?${query}` : "/commits";
}

function getLessonSectionArtifactCount(
  section: LessonSectionData,
  artifactType: LessonArtifact["type"],
) {
  return section.artifacts.filter((artifact) => artifact.type === artifactType)
    .length;
}

function formatArtifactCount(
  count: number,
  singularLabel: string,
  pluralLabel = `${singularLabel}s`,
) {
  return `${count} ${count === 1 ? singularLabel : pluralLabel}`;
}

function getSourceFileMetadata(lesson: LessonData, filePath: SourceFilePath) {
  return (
    lesson.sourceManifest.files.find((sourceFile) => {
      return sourceFile.path === filePath;
    }) ?? getDefaultSourceFileMetadata(lesson)
  );
}

function getDefaultSourceFileMetadata(lesson: LessonData) {
  const [sourceFile] = lesson.sourceManifest.files;

  if (!sourceFile) {
    throw new Error(`Expected lesson ${lesson.id} to include source files.`);
  }

  return sourceFile;
}

interface LessonConceptMention {
  concept: LessonConceptLinkData;
  end: number;
  paragraphIndex: number;
  start: number;
}

interface LessonConceptCandidate extends LessonConceptMention {
  conceptIndex: number;
}

interface TextRange {
  end: number;
  start: number;
}

function getLessonConceptPresentation(section: LessonSectionData): {
  concepts: readonly LessonConceptLinkData[];
  mentions: readonly LessonConceptMention[];
} {
  const concepts = section.concepts ?? [];
  const candidates: LessonConceptCandidate[] = [];

  section.body.forEach((paragraph, paragraphIndex) => {
    const normalizedParagraph = paragraph.toLowerCase();
    const inlineCodeRanges = getInlineCodeRanges(paragraph);

    concepts.forEach((concept, conceptIndex) => {
      concept.terms.forEach((term) => {
        const normalizedTerm = term.toLowerCase();
        let start = normalizedParagraph.indexOf(normalizedTerm);

        while (start !== -1) {
          const end = start + normalizedTerm.length;

          if (
            hasConceptTermBoundaries(paragraph, start, end) &&
            !inlineCodeRanges.some((range) =>
              rangesOverlap(range, { start, end }),
            )
          ) {
            candidates.push({
              concept,
              conceptIndex,
              end,
              paragraphIndex,
              start,
            });
          }

          start = normalizedParagraph.indexOf(normalizedTerm, start + 1);
        }
      });
    });
  });

  candidates.sort((left, right) => {
    return (
      left.paragraphIndex - right.paragraphIndex ||
      left.start - right.start ||
      right.end - right.start - (left.end - left.start) ||
      left.conceptIndex - right.conceptIndex
    );
  });

  const selectedConceptHrefs = new Set<string>();
  const mentions: LessonConceptMention[] = [];

  candidates.forEach((candidate) => {
    if (selectedConceptHrefs.has(candidate.concept.href)) {
      return;
    }

    const overlapsSelectedMention = mentions.some((mention) => {
      return (
        mention.paragraphIndex === candidate.paragraphIndex &&
        rangesOverlap(mention, candidate)
      );
    });

    if (overlapsSelectedMention) {
      return;
    }

    selectedConceptHrefs.add(candidate.concept.href);
    mentions.push({
      concept: candidate.concept,
      end: candidate.end,
      paragraphIndex: candidate.paragraphIndex,
      start: candidate.start,
    });
  });

  const conceptIndexes = new Map(
    concepts.map((concept, index) => [concept.href, index]),
  );
  const mentionsByConceptHref = new Map(
    mentions.map((mention) => [mention.concept.href, mention]),
  );
  const orderedConcepts = [...concepts].sort((left, right) => {
    const leftMention = mentionsByConceptHref.get(left.href);
    const rightMention = mentionsByConceptHref.get(right.href);

    if (leftMention && rightMention) {
      return (
        leftMention.paragraphIndex - rightMention.paragraphIndex ||
        leftMention.start - rightMention.start
      );
    }

    if (leftMention) {
      return -1;
    }

    if (rightMention) {
      return 1;
    }

    return (
      (conceptIndexes.get(left.href) ?? 0) -
      (conceptIndexes.get(right.href) ?? 0)
    );
  });

  return { concepts: orderedConcepts, mentions };
}

function getInlineCodeRanges(text: string): TextRange[] {
  return Array.from(text.matchAll(/`[^`]+`/g), (match) => ({
    end: (match.index ?? 0) + match[0].length,
    start: match.index ?? 0,
  }));
}

function hasConceptTermBoundaries(text: string, start: number, end: number) {
  const startsWithWordCharacter = isConceptWordCharacter(text[start]);
  const endsWithWordCharacter = isConceptWordCharacter(text[end - 1]);

  return !(
    (startsWithWordCharacter && isConceptWordCharacter(text[start - 1])) ||
    (endsWithWordCharacter && isConceptWordCharacter(text[end]))
  );
}

function isConceptWordCharacter(character: string | undefined) {
  return Boolean(character && /[\w-]/.test(character));
}

function rangesOverlap(left: TextRange, right: TextRange) {
  return left.start < right.end && right.start < left.end;
}

interface LessonConceptTextProps {
  activeConceptHref: string | null;
  mentions: readonly LessonConceptMention[];
  onPreviewConcept: (conceptHref: string | null) => void;
  onTogglePinnedConcept: (conceptHref: string) => void;
  originPath: string;
  pinnedConceptHref: string | null;
  previewId: string;
  text: string;
}

function LessonConceptText({
  activeConceptHref,
  mentions,
  onPreviewConcept,
  onTogglePinnedConcept,
  originPath,
  pinnedConceptHref,
  previewId,
  text,
}: LessonConceptTextProps) {
  const content = [];
  let offset = 0;

  mentions.forEach((mention) => {
    const isPinned = pinnedConceptHref === mention.concept.href;
    const conceptText = text.slice(mention.start, mention.end);
    const conceptClassName =
      "decoration-muted-foreground/45 hover:bg-accent/60 hover:text-foreground focus-visible:ring-ring rounded-sm underline decoration-dotted underline-offset-4 transition-colors focus-visible:ring-2 focus-visible:outline-none";

    if (mention.start > offset) {
      content.push(
        <InlineText
          key={`text-${offset}`}
          text={text.slice(offset, mention.start)}
        />,
      );
    }

    content.push(
      <span key={`concept-${mention.concept.href}`}>
        <Link
          className={cn(conceptClassName, "lg:hidden")}
          to={buildLearningHref({
            destination: mention.concept.href,
            from: originPath,
          })}
        >
          {conceptText}
        </Link>
        <button
          aria-controls={previewId}
          aria-expanded={activeConceptHref === mention.concept.href}
          aria-pressed={isPinned}
          title={`${isPinned ? "Unpin" : "Pin"} ${mention.concept.title}`}
          type="button"
          className={cn(
            conceptClassName,
            "hidden lg:inline",
            isPinned && "bg-accent/70 text-foreground decoration-foreground/60",
          )}
          onBlur={() => onPreviewConcept(null)}
          onClick={() => onTogglePinnedConcept(mention.concept.href)}
          onFocus={() => onPreviewConcept(mention.concept.href)}
          onMouseEnter={() => onPreviewConcept(mention.concept.href)}
          onMouseLeave={() => onPreviewConcept(null)}
        >
          {conceptText}
        </button>
      </span>,
    );
    offset = mention.end;
  });

  if (offset < text.length) {
    content.push(
      <InlineText key={`text-${offset}`} text={text.slice(offset)} />,
    );
  }

  return <>{content}</>;
}

interface LessonSectionProps {
  canAccessPaidConcepts: boolean;
  isSeparated: boolean;
  lesson: LessonData;
  originPath: string;
  repositoryCommit: CourseCommitData;
  repositoryCommitCount: number;
  section: LessonSectionData;
  sectionIndex: number;
}

function LessonSection({
  canAccessPaidConcepts,
  isSeparated,
  lesson,
  originPath,
  repositoryCommit,
  repositoryCommitCount,
  section,
  sectionIndex,
}: LessonSectionProps) {
  const sectionNumber = String(sectionIndex + 1).padStart(2, "0");
  const conceptPresentation = useMemo(
    () => getLessonConceptPresentation(section),
    [section],
  );
  const concepts = conceptPresentation.concepts;
  const hasConcepts = concepts.length > 0;
  const [pinnedConceptHref, setPinnedConceptHref] = useState<string | null>(
    null,
  );
  const [previewConceptHref, setPreviewConceptHref] = useState<string | null>(
    null,
  );
  const activeConceptHref = previewConceptHref ?? pinnedConceptHref;
  const previewId = `${section.id}-concept-preview`;
  const sectionOriginPath = `${originPath}#${section.id}`;

  function togglePinnedConcept(conceptHref: string) {
    setPinnedConceptHref((currentHref) => {
      return currentHref === conceptHref ? null : conceptHref;
    });
  }

  return (
    <section
      id={section.id}
      className={cn(
        "grid scroll-mt-20 gap-6",
        isSeparated && "border-border/70 border-t pt-8 sm:pt-10",
      )}
    >
      <div className="relative grid gap-4">
        <div className="bg-secondary text-secondary-foreground ring-border/70 flex size-10 items-center justify-center rounded-md font-mono text-sm font-medium shadow-xs ring-1 xl:absolute xl:top-0 xl:-left-16">
          {sectionNumber}
        </div>

        <div
          className={cn(
            "grid gap-6",
            hasConcepts &&
              "lg:grid-cols-[minmax(0,1fr)_15rem] lg:items-start lg:gap-10",
          )}
        >
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h2 className="max-w-3xl text-2xl leading-tight font-semibold text-balance">
                <InlineText text={section.title} />
              </h2>
              <div className="flex shrink-0 items-center gap-1">
                <a
                  className="text-muted-foreground/70 hover:text-muted-foreground inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition"
                  href="#lesson-path"
                >
                  <ArrowUp className="size-3.5" />
                  Lesson path
                </a>
              </div>
            </div>

            <div className="mt-4 grid gap-4">
              {section.body.map((paragraph, paragraphIndex) => (
                <p
                  key={`${section.id}-${paragraphIndex}`}
                  className="text-muted-foreground max-w-3xl leading-7 text-pretty"
                >
                  <LessonConceptText
                    activeConceptHref={activeConceptHref}
                    originPath={sectionOriginPath}
                    pinnedConceptHref={pinnedConceptHref}
                    previewId={previewId}
                    text={paragraph}
                    mentions={conceptPresentation.mentions.filter(
                      (mention) => mention.paragraphIndex === paragraphIndex,
                    )}
                    onPreviewConcept={setPreviewConceptHref}
                    onTogglePinnedConcept={togglePinnedConcept}
                  />
                </p>
              ))}
            </div>
          </div>

          {hasConcepts && (
            <LessonConceptLinks
              activeConceptHref={activeConceptHref}
              canAccessPaidConcepts={canAccessPaidConcepts}
              concepts={concepts}
              originPath={sectionOriginPath}
              pinnedConceptHref={pinnedConceptHref}
              previewId={previewId}
              sectionTitle={section.title.replaceAll("`", "")}
              onPreviewConcept={setPreviewConceptHref}
              onTogglePinnedConcept={togglePinnedConcept}
            />
          )}
        </div>
      </div>

      <div className="grid w-full gap-4">
        {section.artifacts.map((artifact) => (
          <LessonArtifactView
            key={getArtifactKey(artifact)}
            artifact={artifact}
            lesson={lesson}
            originPath={sectionOriginPath}
            repositoryCommit={repositoryCommit}
            repositoryCommitCount={repositoryCommitCount}
          />
        ))}
      </div>
    </section>
  );
}

interface LessonConceptLinksProps {
  activeConceptHref: string | null;
  canAccessPaidConcepts: boolean;
  concepts: readonly LessonConceptLinkData[];
  onPreviewConcept: (conceptHref: string | null) => void;
  onTogglePinnedConcept: (conceptHref: string) => void;
  originPath: string;
  pinnedConceptHref: string | null;
  previewId: string;
  sectionTitle: string;
}

function LessonConceptLinks({
  activeConceptHref,
  canAccessPaidConcepts,
  concepts,
  onPreviewConcept,
  onTogglePinnedConcept,
  originPath,
  pinnedConceptHref,
  previewId,
  sectionTitle,
}: LessonConceptLinksProps) {
  const [actionConceptHref, setActionConceptHref] = useState<string | null>(
    null,
  );
  const activeConcept = concepts.find(
    (concept) => concept.href === activeConceptHref,
  );

  return (
    <nav
      aria-label={`${sectionTitle} concept references`}
      className="border-border border-y py-4 text-sm lg:border-y-0 lg:border-l-2 lg:py-1 lg:pl-4"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setActionConceptHref(null);
          onPreviewConcept(null);
        }
      }}
      onMouseLeave={() => {
        setActionConceptHref(null);
        onPreviewConcept(null);
      }}
    >
      <span className="text-muted-foreground inline-flex items-center gap-1.5 font-normal">
        <BookOpenCheck className="size-4" />
        Related concepts
      </span>
      <ul className="mt-2 grid gap-0.5">
        {concepts.map((concept) => {
          const conceptDefinition = getConceptDefinitionByHref(concept.href);
          const isLocked =
            conceptDefinition?.access === "paid" && !canAccessPaidConcepts;
          const isPinned = pinnedConceptHref === concept.href;
          const isShowingAction = actionConceptHref === concept.href;

          return (
            <li key={concept.title}>
              <Link
                className="text-muted-foreground hover:bg-accent/60 hover:text-foreground grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-sm px-1.5 py-2 font-normal transition lg:hidden"
                to={buildLearningHref({
                  destination: concept.href,
                  from: originPath,
                })}
              >
                <span>
                  <InlineText text={concept.title} />
                </span>
                {isLocked ? (
                  <Lock className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
              </Link>
              <button
                aria-expanded={activeConceptHref === concept.href}
                aria-pressed={isPinned}
                title={`${isPinned ? "Unpin" : "Pin"} ${concept.title}`}
                type="button"
                aria-controls={
                  activeConceptHref === concept.href ? previewId : undefined
                }
                className={cn(
                  "text-muted-foreground hover:bg-accent/60 hover:text-foreground hidden w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-2 rounded-sm py-2 pr-1 pl-1.5 text-left font-normal transition lg:grid",
                  activeConceptHref === concept.href &&
                    "bg-accent/60 text-foreground",
                )}
                onBlur={() => setActionConceptHref(null)}
                onMouseLeave={() => setActionConceptHref(null)}
                onClick={() => {
                  setActionConceptHref(null);
                  onTogglePinnedConcept(concept.href);
                }}
                onFocus={() => {
                  setActionConceptHref(concept.href);
                  onPreviewConcept(concept.href);
                }}
                onMouseEnter={() => {
                  setActionConceptHref(concept.href);
                  onPreviewConcept(concept.href);
                }}
              >
                <span>
                  <InlineText text={concept.title} />
                </span>
                <span aria-hidden="true" className="mt-0.5 size-3.5">
                  {isPinned ? (
                    isShowingAction ? (
                      <PinOff className="size-3.5" />
                    ) : (
                      <Pin className="size-3.5 fill-current" />
                    )
                  ) : isShowingAction ? (
                    <Pin className="size-3.5" />
                  ) : isLocked ? (
                    <Lock className="size-3.5" />
                  ) : (
                    <Info className="size-3.5" />
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <div
        aria-label="Concept preview"
        className="border-border mt-3 hidden border-t pt-3 lg:grid"
        id={previewId}
        role="region"
      >
        {concepts.map((concept) => (
          <div
            key={`preview-size-${concept.href}`}
            aria-hidden="true"
            className="invisible col-start-1 row-start-1"
          >
            <LessonConceptPreviewContent concept={concept} />
          </div>
        ))}

        {activeConcept ? (
          <div className="col-start-1 row-start-1">
            <LessonConceptPreviewContent
              concept={activeConcept}
              originPath={originPath}
            />
          </div>
        ) : (
          <div className="text-muted-foreground/60 col-start-1 row-start-1 flex items-center gap-2 text-xs font-light">
            <Info className="size-3.5 shrink-0" />
            <div className="grid gap-1">
              <p>No concept selected</p>
              <p>Hover to preview. Click to pin.</p>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function LessonConceptPreviewContent({
  concept,
  originPath,
}: {
  concept: LessonConceptLinkData;
  originPath?: string;
}) {
  const actionClassName =
    "underline-decoration inline-flex w-fit items-center gap-1.5 text-sm underline";
  const action = (
    <>
      Read full concept
      <ArrowRight className="size-3.5" />
    </>
  );

  return (
    <div className="grid gap-2">
      <p className="text-sm font-normal">
        <InlineText text={concept.title} />
      </p>
      <p className="text-muted-foreground text-sm leading-5">
        <InlineText text={concept.summary} />
      </p>
      {originPath ? (
        <Link
          className={actionClassName}
          to={buildLearningHref({
            destination: concept.href,
            from: originPath,
          })}
        >
          {action}
        </Link>
      ) : (
        <span className={actionClassName}>{action}</span>
      )}
    </div>
  );
}

function getArtifactKey(artifact: LessonArtifact) {
  if (artifact.type === "code") {
    const commentsKey = artifact.comments
      ? artifact.comments.lineNumbers.join("-")
      : "no-comments";

    return `${artifact.type}-${artifact.filePath}-${artifact.lineNumbers.join("-")}-${commentsKey}`;
  }

  return `${artifact.type}-${artifact.title}`;
}

interface LessonArtifactViewProps {
  artifact: LessonArtifact;
  lesson: LessonData;
  originPath: string;
  repositoryCommit: CourseCommitData;
  repositoryCommitCount: number;
}

function LessonArtifactView({
  artifact,
  lesson,
  originPath,
  repositoryCommit,
  repositoryCommitCount,
}: LessonArtifactViewProps) {
  if (artifact.type === "code") {
    return (
      <CodeClip
        artifact={artifact}
        lesson={lesson}
        originPath={originPath}
        repositoryCommit={repositoryCommit}
        repositoryCommitCount={repositoryCommitCount}
      />
    );
  }

  return (
    <TerminalReplay
      context={artifact.context}
      steps={artifact.steps}
      title={artifact.title}
    />
  );
}

interface CodeClipProps {
  artifact: CodeArtifact;
  lesson: LessonData;
  originPath: string;
  repositoryCommit: CourseCommitData;
  repositoryCommitCount: number;
}

function CodeClip({
  artifact,
  lesson,
  originPath,
  repositoryCommit,
  repositoryCommitCount,
}: CodeClipProps) {
  const [areCommentsVisible, setAreCommentsVisible] = useState(true);
  const sourceFileMetadata = getSourceFileMetadata(lesson, artifact.filePath);
  const sourceFileState = useSourceFileContent({
    commit: lesson.commit,
    filePath: artifact.filePath,
    sourceFileMetadata,
  });
  const detectedCommentLineNumbers =
    sourceFileState.status === "ready"
      ? getCodeClipCommentLineNumbers(
          sourceFileState.sourceFile,
          artifact.lineNumbers,
        )
      : [];
  const commentLineNumbers = new Set([
    ...(artifact.comments?.lineNumbers ?? []),
    ...detectedCommentLineNumbers,
  ]);
  const visibleLineNumbers = areCommentsVisible
    ? artifact.lineNumbers
    : artifact.lineNumbers.filter((lineNumber) => {
        return !commentLineNumbers.has(lineNumber);
      });
  const rows =
    sourceFileState.status === "ready"
      ? buildCodeClipRows(
          sourceFileState.sourceFile,
          visibleLineNumbers,
          areCommentsVisible ? [] : commentLineNumbers,
        )
      : [];
  const lineRange = getLineRange(artifact.lineNumbers);
  const selectedLineNumbers = new Set(artifact.lineNumbers);
  const selectedLineCount = selectedLineNumbers.size;
  const commentLineCount = Array.from(commentLineNumbers).filter((lineNumber) =>
    selectedLineNumbers.has(lineNumber),
  ).length;
  const canToggleComments = commentLineCount > 0;
  const visibleLineCount = areCommentsVisible
    ? selectedLineCount
    : selectedLineCount - commentLineCount;

  return (
    <div className="bg-card overflow-hidden rounded-md border shadow-sm">
      <div className="bg-muted/30 flex min-h-14 flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 items-start gap-2">
          <Code2 className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div className="min-w-0">
            <p className="truncate font-mono text-xs">
              {artifact.filePath}
              {lineRange && (
                <span className="text-muted-foreground">:{lineRange}</span>
              )}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              {visibleLineCount} focused{" "}
              {visibleLineCount === 1 ? "line" : "lines"}
              {canToggleComments && !areCommentsVisible && (
                <> · {commentLineCount} comments hidden</>
              )}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                asChild
                className="hidden font-mono sm:inline-flex"
                variant="outline"
              >
                <Link
                  to={buildRepositoryCommitHref({
                    commit: lesson.commit,
                    from: originPath,
                  })}
                >
                  <GitCommit />
                  {repositoryCommit.shortCommit}
                </Link>
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              className="grid max-w-72 gap-1"
              side="top"
              sideOffset={6}
            >
              <span>{repositoryCommit.subject}</span>
              <span className="opacity-75">
                {repositoryCommit.date} · Commit{" "}
                {repositoryCommit.timelineOrder + 1} of {repositoryCommitCount}
              </span>
            </TooltipContent>
          </Tooltip>
          {lineRange && (
            <Badge
              className="hidden font-mono sm:inline-flex"
              variant="outline"
            >
              L{lineRange}
            </Badge>
          )}
          {canToggleComments && (
            <Button
              className="h-8 gap-1.5 px-2 text-xs"
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => setAreCommentsVisible((isVisible) => !isVisible)}
            >
              {areCommentsVisible ? (
                <MessageSquareOff className="size-3.5" />
              ) : (
                <MessageSquareText className="size-3.5" />
              )}
              {areCommentsVisible ? "Hide comments" : "Show comments"}
            </Button>
          )}
          <Button
            asChild
            className="h-8 gap-1.5 px-2 text-xs"
            size="sm"
            variant="ghost"
          >
            <Link
              title={`${repositoryCommit.subject} · ${repositoryCommit.date} · commit ${repositoryCommit.timelineOrder + 1} of ${repositoryCommitCount}`}
              to={buildRepositoryCommitHref({
                commit: lesson.commit,
                filePath: artifact.filePath,
                from: originPath,
                lineNumbers: artifact.lineNumbers,
              })}
            >
              <GitCommit className="size-3.5" />
              View in repository
            </Link>
          </Button>
        </div>
      </div>

      <div className="bg-background overflow-x-auto p-0 text-[13px] leading-6">
        {sourceFileState.status === "loading" && (
          <SourceStatusRow label="Loading source..." />
        )}
        {sourceFileState.status === "error" && (
          <SourceStatusRow label={sourceFileState.message} />
        )}
        {rows.map((row) => {
          if (row.kind === "gap") {
            return <CodeClipGap key={row.key} />;
          }

          return (
            <CodeClipLine
              key={`${artifact.filePath}-${row.lineNumber}`}
              language={getSourceLanguage(sourceFileMetadata)}
              line={row}
            />
          );
        })}
      </div>
    </div>
  );
}

interface CodeClipLineProps {
  language: SourceSnapshotFileManifest["language"];
  line: CodeClipLineRow;
}

function CodeClipGap() {
  return (
    <div
      aria-label="Omitted source lines"
      className="bg-muted/30 text-muted-foreground grid min-w-full grid-cols-[3.25rem_minmax(0,1fr)] font-mono text-xs"
      style={codeFontStyle}
    >
      <span className="border-r px-3 text-right select-none">...</span>
      <span className="px-4 select-none">...</span>
    </div>
  );
}

function CodeClipLine({ language, line }: CodeClipLineProps) {
  return (
    <code
      style={codeFontStyle}
      className={cn(
        "grid min-w-max grid-cols-[3.25rem_minmax(0,1fr)] font-mono transition-colors",
        line.isFocused ? "bg-secondary/50" : "bg-background",
      )}
    >
      <span
        className={cn(
          "border-r px-3 text-right select-none",
          line.isFocused ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {line.lineNumber}
      </span>
      <span className="px-4 whitespace-pre">
        <CodeLine language={language} line={line.text} />
        {line.text.length === 0 && "\u00a0"}
      </span>
    </code>
  );
}

interface CodeClipGapRow {
  key: string;
  kind: "gap";
}

interface CodeClipLineRow {
  isFocused: boolean;
  kind: "line";
  lineNumber: number;
  text: string;
}

type CodeClipRow = CodeClipGapRow | CodeClipLineRow;

function buildCodeClipRows(
  sourceFile: SourceFileContent,
  lineNumbers: readonly number[],
  hiddenLineNumbers: ReadonlySet<number> | readonly number[] = [],
) {
  const hiddenLines = new Set(hiddenLineNumbers);
  const uniqueLineNumbers = Array.from(new Set(lineNumbers)).sort(
    (first, second) => first - second,
  );
  const rows: CodeClipRow[] = [];

  for (const lineNumber of uniqueLineNumbers) {
    if (lineNumber < 1 || lineNumber > sourceFile.lines.length) {
      continue;
    }

    const previousRow = rows.at(-1);

    if (
      previousRow &&
      previousRow.kind === "line" &&
      lineNumber > previousRow.lineNumber + 1
    ) {
      const omittedLineNumbers = Array.from(
        { length: lineNumber - previousRow.lineNumber - 1 },
        (_, index) => previousRow.lineNumber + index + 1,
      );

      if (
        shouldInlineCodeClipGap(sourceFile, omittedLineNumbers, hiddenLines)
      ) {
        rows.push(
          ...omittedLineNumbers.map((omittedLineNumber) => ({
            isFocused: false,
            kind: "line" as const,
            lineNumber: omittedLineNumber,
            text: getSourceLineText(sourceFile, omittedLineNumber),
          })),
        );
      } else {
        rows.push({
          key: `${sourceFile.path}-${previousRow.lineNumber}-${lineNumber}`,
          kind: "gap",
        });
      }
    }

    rows.push({
      isFocused: true,
      kind: "line",
      lineNumber,
      text: getSourceLineText(sourceFile, lineNumber),
    });
  }

  return rows;
}

function shouldInlineCodeClipGap(
  sourceFile: SourceFileContent,
  omittedLineNumbers: readonly number[],
  hiddenLineNumbers: ReadonlySet<number>,
) {
  return (
    omittedLineNumbers.length === 1 &&
    !hiddenLineNumbers.has(omittedLineNumbers[0] ?? 0) &&
    omittedLineNumbers.every((lineNumber) => {
      return isCodeClipSeparatorLine(getSourceLineText(sourceFile, lineNumber));
    })
  );
}

// Let every Make and shell clip hide full-line comments without requiring each
// lesson to repeat source-specific metadata. Markdown headings and shebangs are
// structural content, so they remain visible.
function getCodeClipCommentLineNumbers(
  sourceFile: SourceFileContent,
  lineNumbers: readonly number[],
) {
  const language = getSourceLanguage(sourceFile);

  if (language !== "makefile" && language !== "shellscript") {
    return [];
  }

  return lineNumbers.filter((lineNumber) => {
    const line = getSourceLineText(sourceFile, lineNumber);

    return /^\s*#(?!!)/.test(line);
  });
}

function isCodeClipSeparatorLine(line: string) {
  return line.trim() === "" || /^\s*(#|\/\/)\s*$/.test(line);
}

function getSourceLineText(sourceFile: SourceFileContent, lineNumber: number) {
  return sourceFile.lines[lineNumber - 1] ?? "";
}
